import Image from '@atoms/Image';
import Button from '@atoms/new/button';
import Input from '@atoms/new/input';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@atoms/new/modal';
import { pushDataLayerForEvent } from '@lib/gtag';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { Avatar } from '@heroui/react';
import { userLogin, userLoginFromSandbox, verifyLogin, verifyLoginFromSandbox } from '@services/youtube-platform.service';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { yupValidator } from '@lib/yup-validator';
import * as yup from 'yup';
import { useToggle } from '@uidotdev/usehooks';
import { setAuth } from '@services/identity.service';
import useClientInfo from '@lib/hooks/useClientInfo';
import Alert from '@atoms/Alert';

import { useGoogleLogin } from '@react-oauth/google';
import { GoogleLogoIcon as GoogleIcon } from '@phosphor-icons/react';

interface SignUpModalProps {
  isOpen: boolean;
  onClose?: (data?: object) => void;
  onOpenChange: (isOpen: boolean) => void;
  isSandbox: boolean;
  theme?: string;
  platform: string;
}

interface IFormValues {
  email: string;
  code: string;
}

export const SignUpModal = ({
  isOpen,
  onClose,
  onOpenChange,
  isSandbox = true,
  theme,
  platform = 'sandbox',
}: SignUpModalProps) => {
  const initTeam = useTeamsData((s) => s.initTeam);

  const [isSuccess, setIsSuccess] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [resendCodeTimer, setResendCodeTimer] = useState(30);
  const [error, setError] = useState('');
  const clientInfo = useClientInfo();

  useEffect(() => {
    if (!isCodeSent) return;

    const timer = setInterval(() => {
      setResendCodeTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isCodeSent]);

  useEffect(() => {
    if (resendCodeTimer === 0) {
      setIsCodeSent(false);
      setResendCodeTimer(30);
    }
  }, [resendCodeTimer]);

  const sendCode = () => {
    if (!isSuccess) {
      setIsSuccess((prev) => !prev);
    } else {
      setIsCodeSent(true);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: (codeResponse) => {
      const url = `${process.env.NEXT_PUBLIC_MAIN_APP_URL}/auth/google?code=${codeResponse?.access_token}`;
      window.location.href = url;
    },
    onError: () => console.error('Login Failed'),
  });

  const {
    watch,
    register,
    handleSubmit,
    setError: _setFormError,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupValidator(
      yup.object().shape({
        email: yup
          .string()
          .required('Please enter email.')
          .email('Please enter a valid email.'),
      })
    ),
  });

  const watchEmail = watch('email');

  useEffect(() => {
    if (!watchEmail) return;
    setIsSuccess(false);
    setIsCodeSent(false);
  }, [watchEmail]);

  const validateCode = (code: string = '') => {
    if (!code) return false;

    if (code.length !== 6) {
      _setFormError('code', { message: 'Please enter a valid code.' });
      return false;
    }

    try {
      parseInt(code);
    } catch (error: any) {
      _setFormError('code', { message: 'Please enter a valid code.' });
      return false;
    }

    return true;
  };

  const getLoginCode = async (email: string) => {
    let loginFunction = userLoginFromSandbox;
    if (!isSandbox) {
      loginFunction = userLogin;
    }
    const res = await loginFunction({ email, accessType: 'signup' });
    pushDataLayerForEvent('signup_started', { email }, false);

    if (!res.status && res.message) {
      throw new Error(res.message[0]);
    }

    if (!res.status) {
      throw new Error('Something went wrong. Please try again.');
    }
  };

  const [loading, toogleLoading] = useToggle(false);

  const verifyLoginCode = async ({ email, code }: any) => {
    toogleLoading(true);
    let verifyFunction = verifyLoginFromSandbox;
    if (!isSandbox) {
      verifyFunction = verifyLogin;
    }
    try {
      const res = await verifyFunction({
        email,
        inputCode: code,
        accessType: 'login',
        clientInfo,
      });
      if (res.status) {
        pushDataLayerForEvent('signup_completed', { email }, false);

        localStorage.setItem('user_email', email);
        localStorage.setItem('user_id', res.entity[0]?.id);
        localStorage.setItem('hash', res.entity[0]?.hash);
        // @ts-expect-error fpr might not exist on window.
        const firstpromoter = window.fpr;
        if (firstpromoter) {
          firstpromoter('referral', { email });
        }
        // @ts-expect-error lintrk might not exist on window.
        const lintrk = window.lintrk;
        if (lintrk) {
          lintrk('track', { conversion_id: 10938372 });
        }
        setError('');
        if (isSandbox) {
          window.open(`${process.env.NEXT_PUBLIC_MAIN_APP_URL}/?token=${res?.entity[0]?.token}`, '_blank');
        } else {
          await setAuth(res.entity[0]);
          await initTeam();
        }
        onClose?.(res.entity[0]);
      } else {
        setError('Invalid code. Please try again.');
      }
    } catch (e) {
      console.log({ error });
    } finally {
      toogleLoading(false);
    }
  };

  const onSubmit: SubmitHandler<IFormValues> = async ({ email, code }) => {
    try {
      setError('');
      if (!isSuccess) {
        pushDataLayerForEvent('signup_started', { email }, false);
        await getLoginCode(email);
        setIsSuccess(true);
        return;
      }

      if (!code) {
        _setFormError('code', { message: 'Please enter a valid code.' });
        return false;
      }

      const valid = validateCode(code);

      if (!valid) return;

      verifyLoginCode({ email, code });
    } catch (error: any) {
      setError(error.message);
    }
  };

  useEffect(() => {
    if (!errors) return;

    setError(errors.email?.message || errors.code?.message || '');
  }, [errors]);

  useEffect(() => {
    pushDataLayerForEvent(`${platform}_signup_form`, {}, false);
  }, []);

  return (
    <Modal
      size="sm"
      isOpen={isOpen}
      isDismissable={false}
      isKeyboardDismissDisabled={false}
      onOpenChange={onOpenChange}
      onClose={onClose}
      className="p-0"
      theme={theme}
    >
      <ModalContent>
        <div className="p-7.5">
          <ModalHeader>Loving what you see?</ModalHeader>

          <ModalBody>
            <p className="font-normal text-sm text-grey-800 dark:text-grey-200 mb-4 m-0">
              Add these interactions to your next live session. Unlimited
              interactions for smaller audiences in Free Plan.
            </p>

            {error && (
              <div className='mb-4'>
                <Alert variant='alert' header={error} textColor='text-neutral-110' />
              </div>
            )}

            <div>
              <Button
                className='w-full'
                startContent={<GoogleIcon />}
                onPress={() => googleLogin()}
              >
                Continue with Google
              </Button>
              <div className='flex items-center my-6'>
                <div className='flex-1 border border-grey-200'></div>
                <p className='text-grey-800 dark:text-grey-200 text-center font-normal text-sm mx-2'>or</p>
                <div className='flex-1 border border-grey-200'></div>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="pb-4">
                <Input
                  {...register('email')}
                  label="Email"
                  labelPlacement="outside"
                  placeholder="someone@example.com"
                  state={errors.email ? 'invalid' : 'default'}
                />
              </div>

              {isSuccess && (
                <div className="pb-4">
                  <div>
                    <Input
                      {...register('code')}
                      className="mb-2"
                      required
                      label="Signup code"
                      labelPlacement="outside"
                      placeholder="Paste signup code"
                      state={errors.code ? 'invalid' : 'default'}
                    />
                  </div>
                  <p className="text-grey-700 text-sm font-normal">
                    We sent a signup code to your inbox ·
                    {isCodeSent ? (
                      <span>{` ${resendCodeTimer}s`}</span>
                    ) : (
                      <span
                        onClick={sendCode}
                        className="text-blue-600 cursor-pointer"
                      >
                        {' Resend'}
                      </span>
                    )}
                  </p>
                </div>
              )}

              <Button kind='secondary' type="submit" fullWidth isLoading={loading}>
                {isSuccess ? 'Create new account' : 'Get signup code'}
              </Button>

              <p className="font-normal text-sm text-center text-grey-700 dark:text-grey-200 pt-4">
                {'By signing up, you agree to the '}
                <span
                  onClick={() =>
                    window.open(
                      'https://www.streamalive.com/terms-of-service',
                      '_blank'
                    )
                  }
                  className="font-medium cursor-pointer"
                >
                  Terms
                </span>
                {' and '}
                <span
                  onClick={() =>
                    window.open('https://www.streamalive.com/privacy', '_blank')
                  }
                  className="font-medium cursor-pointer"
                >
                  Privacy Policy
                </span>
              </p>
            </form>
          </ModalBody>
        </div>
        <ModalFooter className="p-6 bg-grey-50">
          <div className="w-full">
            <p className="text-center font-semibold text-grey-900 text-sm">
              Trusted by companies, educators and creators
            </p>

            <div className="flex items-center justify-around my-6">
              <Image
                className="grayscale-50 opacity-65"
                width={76}
                height={23}
                src={'/images/sandbox/website/g12.png'}
              />
              <Image
                className="grayscale-50 opacity-65"
                width={55}
                height={28}
                src={'/images/sandbox/website/image 3.png'}
              />
              <Image
                className="grayscale-50 opacity-65"
                width={56}
                height={20}
                src={'/images/sandbox/website/Logo_NIKE 1.png'}
              />
              <Image
                className="grayscale-50 opacity-65"
                width={116}
                height={22}
                src={'/images/sandbox/website/Group 1.png'}
              />
            </div>

            <div className="flex items-center justify-around my-6">
              <Avatar
                className="grayscale-50 opacity-65"
                src={'/images/sandbox/website/image 7.png'}
                size="md"
              />
              <Avatar
                className="grayscale-50 opacity-65"
                src={'/images/sandbox/website/image 6.png'}
                size="md"
              />
              <Avatar
                className="grayscale-50 opacity-65"
                src={'/images/sandbox/website/image 8.png'}
                size="md"
              />
              <Avatar
                className="grayscale-50 opacity-65"
                src={'/images/sandbox/website/image 5.png'}
                size="md"
              />
              <Avatar
                className="grayscale-50 opacity-65"
                src={'/images/sandbox/website/image 4.png'}
                size="md"
              />
            </div>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
